import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap-v5";
import { MdEdit, MdOutlineDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";
import { DataService } from "../../Services/dataService";
import Loader from "../../Components/Loader";

const DEFAULT_SECTIONS = ["Menu", "Cargo Solutions", "Reports", "Management"];
const SECTION_STORAGE_KEY = "saas_menu_sections";

const readSavedSections = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SECTION_STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved.filter((name) => typeof name === "string" && name.trim()) : [];
  } catch (err) {
    return [];
  }
};

export const MenuList = () => {
  const [menus, setMenus] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [customSections, setCustomSections] = useState(readSavedSections);
  const [newSection, setNewSection] = useState("");
  const [formData, setFormData] = useState({
    menu_id: "",
    menu_title: "",
    menu_url: "",
    menu_icon: "Activity",
    menu_order: 0,
    menu_section: "Menu",
    menu_status: "ACTIVE",
  });

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = (searchTerm = search) => {
    setLoader(true);
    DataService.get(
      "/admin/get_saas_menu",
      searchTerm && searchTerm.trim() ? { search: searchTerm.trim() } : false
    )
      .then((res) => {
        setLoader(false);
        // DataService interceptor already returns response.data
        if (res && res.status) {
          setMenus(Array.isArray(res.data) ? res.data : []);
        } else {
          toast.error(res?.message || "Failed to load menus");
        }
      })
      .catch((err) => {
        setLoader(false);
        console.error(err);
        toast.error("Failed to load menus");
      });
  };

  const handleShow = (menu = null) => {
    if (menu) {
      setIsEdit(true);
      setFormData({
        menu_id: menu._id,
        menu_title: menu.menu_title || "",
        menu_url: menu.menu_url || "",
        menu_icon: menu.menu_icon || "Activity",
        menu_order: menu.menu_order || 0,
        menu_section: menu.menu_section || "Menu",
        menu_status: menu.menu_status || "ACTIVE",
      });
    } else {
      setIsEdit(false);
      setFormData({
        menu_id: "",
        menu_title: "",
        menu_url: "",
        menu_icon: "Activity",
        menu_order: menus.length + 1,
        menu_section: "Menu",
        menu_status: "ACTIVE",
      });
    }
    setNewSection("");
    setShowModal(true);
  };

  const handleClose = () => {
    setNewSection("");
    setShowModal(false);
  };

  const sectionNames = Array.from(new Set([
    ...DEFAULT_SECTIONS,
    ...customSections,
    ...menus.map((menu) => (menu.menu_section || "").trim()).filter(Boolean),
  ]));

  const saveSection = (name) => {
    const section = String(name || "").trim();
    if (!section) return "";
    setCustomSections((current) => {
      if (current.some((item) => item.toLowerCase() === section.toLowerCase()) || DEFAULT_SECTIONS.some((item) => item.toLowerCase() === section.toLowerCase())) {
        return current;
      }
      const next = [...current, section];
      localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    return section;
  };

  const handleAddSection = () => {
    const section = saveSection(newSection);
    if (!section) return;
    setFormData((current) => ({ ...current, menu_section: section }));
    setNewSection("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoader(true);
    const endpoint = isEdit ? "/admin/update_saas_menu" : "/admin/add_saas_menu";

    saveSection(formData.menu_section);
    DataService.post(endpoint, formData)
      .then((res) => {
        setLoader(false);
        if (res && res.status) {
          toast.success(res.message || "Menu saved successfully");
          handleClose();
          fetchMenus();
        } else {
          toast.error(res?.message || "Error saving menu");
        }
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.message || "An error occurred");
      });
  };

  const handleStatusChange = (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    DataService.post("/admin/update_saas_menu_status", {
      menu_id: id,
      menu_status: newStatus,
    })
      .then((res) => {
        if (res && res.status) {
          toast.success("Status updated");
          fetchMenus();
        } else {
          toast.error(res?.message || "Failed to update status");
        }
      })
      .catch(console.error);
  };

  const handleDelete = (menu) => {
    if (
      !window.confirm(
        `Delete menu "${menu.menu_title}" (${menu.menu_url})?\nThis will also remove it from employee role permissions.`
      )
    ) {
      return;
    }

    setLoader(true);
    DataService.post("/admin/delete_saas_menu", { menu_id: menu._id })
      .then((res) => {
        setLoader(false);
        if (res && res.status) {
          toast.success(res.message || "Menu deleted");
          fetchMenus();
        } else {
          toast.error(res?.message || "Failed to delete menu");
        }
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.message || "Failed to delete menu");
      });
  };

  return (
    <>
      <div className="content-wrapper px-3 py-3">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h4 className="mb-1">Menu Management</h4>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Manage SaaS / retailer portal menus — title, route, icon, order, and status.
            </p>
          </div>
          <Button variant="primary" onClick={() => handleShow()}>
            Add Menu
          </Button>
        </div>

        <div className="d-flex gap-2 mb-3">
          <Form.Control
            type="text"
            placeholder="Search by title, route, or icon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <Button variant="outline-secondary" onClick={() => fetchMenus(search)}>
            Search
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearch("");
              fetchMenus("");
            }}
          >
            Reset
          </Button>
        </div>

        <div className="table-responsive bg-white p-3 rounded shadow-sm">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>URL (Route)</th>
                <th>Section</th>
                <th>Icon</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sectionNames.map((section) => {
                const sectionMenus = menus.filter((menu) => (menu.menu_section || "Menu") === section);
                if (sectionMenus.length === 0 && !customSections.includes(section) && section !== "Reports") return null;
                return (
                  <React.Fragment key={section}>
                    <tr>
                      <td colSpan="7" className="fw-bold" style={{ background: "#f8fafc", color: "#475569" }}>
                        {section}
                        {sectionMenus.length === 0 ? " — no menus yet" : ""}
                      </td>
                    </tr>
                    {sectionMenus.map((m) => (
                <tr key={m._id}>
                  <td>{m.menu_order}</td>
                  <td className="fw-semibold">{m.menu_title}</td>
                  <td>
                    <code style={{ fontSize: "0.8rem" }}>{m.menu_url}</code>
                  </td>
                  <td>{m.menu_section || "Menu"}</td>
                  <td>{m.menu_icon}</td>
                  <td>
                    <span
                      className={
                        "badge bg-" +
                        (m.menu_status === "ACTIVE" ? "success" : "secondary")
                      }
                      style={{ cursor: "pointer" }}
                      title="Click to toggle status"
                      onClick={() => handleStatusChange(m._id, m.menu_status)}
                    >
                      {m.menu_status || "ACTIVE"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-info me-2"
                      onClick={() => handleShow(m)}
                      title="Edit"
                    >
                      <MdEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(m)}
                      title="Delete"
                    >
                      <MdOutlineDeleteForever />
                    </button>
                  </td>
                </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {menus.length === 0 && !loader && (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No menus found. Add one or seed default menus via /admin/seed_menus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Menu" : "Add Menu"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Menu Title</Form.Label>
              <Form.Control
                type="text"
                name="menu_title"
                value={formData.menu_title}
                onChange={handleChange}
                placeholder="e.g. Freight Calculator"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>URL (Route Path)</Form.Label>
              <Form.Control
                type="text"
                name="menu_url"
                value={formData.menu_url}
                onChange={handleChange}
                placeholder="e.g. /dashboard/calculator"
                required
              />
              <Form.Text muted>
                Must match the SaaS app route used in employee permissions.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Icon Name (Lucide)</Form.Label>
              <Form.Control
                type="text"
                name="menu_icon"
                value={formData.menu_icon}
                onChange={handleChange}
                placeholder="e.g. Calculator, BarChart3, Truck"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Section</Form.Label>
              <Form.Select
                name="menu_section"
                value={formData.menu_section}
                onChange={handleChange}
              >
                {sectionNames.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </Form.Select>
              <div className="d-flex gap-2 mt-2">
                <Form.Control
                  type="text"
                  placeholder="New section name"
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                />
                <Button type="button" variant="outline-primary" onClick={handleAddSection} style={{ whiteSpace: "nowrap" }}>
                  Add Section
                </Button>
              </div>
              <Form.Text muted>
                Add a section here, then save the menu. SaaS shows it under that section.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Order Position</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="menu_order"
                value={formData.menu_order}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="menu_status"
                value={formData.menu_status}
                onChange={handleChange}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {loader && <Loader />}
    </>
  );
};

export default MenuList;
